import { css, fillOutlined, circle, shade } from './draw-utils';
import { glow, lumpPath, makeSheet } from './env-kit';

/**
 * Wall-dressing props per sector theme (6 variants each, docs/04). Raised
 * clutter is ONLY placed in the wall band (arena/decor.ts) — anything that
 * looks solid either collides or sits where the ship cannot go. Drawn
 * "up" = toward the room (the placer rotates them to face it).
 */
const SIZE = 128;
const FRAMES = 6;

function box(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
}

export const envPropScrap = makeSheet('env/prop-scrap', SIZE, FRAMES, (ctx, c, f) => {
  switch (f) {
    case 0: // crate
      box(ctx, c - 26, c - 26, 52, 52, 6);
      fillOutlined(ctx, 0x6b5a3e, 5);
      ctx.strokeStyle = css(0x4a3e2a);
      ctx.lineWidth = 4;
      ctx.beginPath(); ctx.moveTo(c - 22, c - 22); ctx.lineTo(c + 22, c + 22); ctx.stroke();
      break;
    case 1: // barrel
      circle(ctx, c, c, 24);
      fillOutlined(ctx, 0x8a3f2e, 5);
      circle(ctx, c, c, 15);
      ctx.strokeStyle = css(0x5c2a1f);
      ctx.lineWidth = 3;
      ctx.stroke();
      circle(ctx, c - 7, c - 7, 4);
      ctx.fillStyle = css(0xf5a623);
      ctx.fill();
      break;
    case 2: // pipe stack
      for (let i = 0; i < 3; i++) {
        box(ctx, c - 34, c - 22 + i * 15, 68, 13, 6);
        fillOutlined(ctx, i === 1 ? 0x56656c : 0x6f8088, 4);
      }
      break;
    case 3: // gear
      ctx.beginPath();
      for (let i = 0; i < 16; i++) {
        const a = (Math.PI * 2 * i) / 16;
        const r = i % 2 === 0 ? 30 : 23;
        ctx.lineTo(c + Math.cos(a) * r, c + Math.sin(a) * r);
      }
      ctx.closePath();
      fillOutlined(ctx, 0x7a8b92, 5);
      circle(ctx, c, c, 9);
      fillOutlined(ctx, 0x2d373c, 3);
      break;
    case 4: // cable spool
      circle(ctx, c, c, 26);
      fillOutlined(ctx, 0x3b474d, 5);
      for (let r = 20; r > 6; r -= 5) {
        circle(ctx, c, c, r);
        ctx.strokeStyle = css(r % 10 === 0 ? 0xd92b2b : 0x2a6b8a);
        ctx.lineWidth = 3;
        ctx.stroke();
      }
      break;
    default: // broken panel with a lamp
      ctx.beginPath();
      ctx.moveTo(c - 30, c + 20); ctx.lineTo(c - 22, c - 26); ctx.lineTo(c + 28, c - 18);
      ctx.lineTo(c + 18, c + 6); ctx.lineTo(c + 30, c + 24);
      ctx.closePath();
      fillOutlined(ctx, 0x4f5e65, 5);
      glow(ctx, c - 2, c - 6, 14, '255, 190, 90', 0.9);
  }
});

export const envPropCore = makeSheet('env/prop-core', SIZE, FRAMES, (ctx, c, f) => {
  const crystal = (color: number, n: number): void => {
    for (let i = 0; i < n; i++) {
      const a = -Math.PI / 2 + (i - (n - 1) / 2) * 0.45;
      const len = 34 - Math.abs(i - (n - 1) / 2) * 6;
      ctx.beginPath();
      ctx.moveTo(c + Math.cos(a - 0.2) * 8, c + 14 + Math.sin(a - 0.2) * 8);
      ctx.lineTo(c + Math.cos(a) * len, c + 14 + Math.sin(a) * len);
      ctx.lineTo(c + Math.cos(a + 0.2) * 8, c + 14 + Math.sin(a + 0.2) * 8);
      ctx.closePath();
      fillOutlined(ctx, i % 2 === 0 ? color : shade(color, 1.25), 4);
    }
  };
  switch (f) {
    case 0: crystal(0xff8a3c, 3); break;
    case 1: crystal(0xffd27a, 4); break;
    case 2: // steam vent
      circle(ctx, c, c, 22);
      fillOutlined(ctx, 0x3a2629, 5);
      circle(ctx, c, c, 11);
      ctx.fillStyle = css(0x140b0e);
      ctx.fill();
      glow(ctx, c, c, 26, '255, 230, 210', 0.25);
      break;
    case 3: // basalt column top
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i;
        ctx.lineTo(c + Math.cos(a) * 26, c + Math.sin(a) * 26);
      }
      ctx.closePath();
      fillOutlined(ctx, 0x4a3438, 5);
      circle(ctx, c - 6, c - 6, 9);
      ctx.fillStyle = css(0x664649);
      ctx.fill();
      break;
    case 4: // ember brazier
      circle(ctx, c, c, 22);
      fillOutlined(ctx, 0x2a1d22, 5);
      glow(ctx, c, c, 20, '255, 140, 50', 0.95);
      break;
    default: // obsidian shard
      ctx.beginPath();
      ctx.moveTo(c - 18, c + 20); ctx.lineTo(c - 4, c - 30); ctx.lineTo(c + 20, c + 16);
      ctx.closePath();
      fillOutlined(ctx, 0x1f1418, 5);
      ctx.strokeStyle = 'rgba(255, 140, 60, 0.6)';
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(c - 4, c - 24); ctx.lineTo(c + 2, c + 12); ctx.stroke();
  }
});

export const envPropBrood = makeSheet('env/prop-brood', SIZE, FRAMES, (ctx, c, f) => {
  switch (f) {
    case 0: // egg sac
      ctx.beginPath();
      ctx.ellipse(c, c, 20, 26, 0, 0, Math.PI * 2);
      fillOutlined(ctx, 0xb8d68a, 5);
      glow(ctx, c, c + 4, 16, '200, 255, 120', 0.7);
      break;
    case 1: // tendril clump
      for (let i = 0; i < 4; i++) {
        const a = -Math.PI / 2 + (i - 1.5) * 0.5;
        ctx.beginPath();
        ctx.moveTo(c, c + 18);
        ctx.quadraticCurveTo(c + Math.cos(a + 0.6) * 22, c + Math.sin(a + 0.6) * 22,
          c + Math.cos(a) * 36, c + Math.sin(a) * 36);
        ctx.strokeStyle = css(0x1d1430);
        ctx.lineWidth = 9;
        ctx.lineCap = 'round';
        ctx.stroke();
        ctx.strokeStyle = css(0x8a4a78);
        ctx.lineWidth = 5;
        ctx.stroke();
      }
      break;
    case 2: // spore pod
      lumpPath(ctx, c, c, 24, 10, 0.1, 0.3);
      fillOutlined(ctx, 0x4d6e3c, 5);
      for (const [x, y] of [[-8, -6], [8, -2], [0, 10]] as const) glow(ctx, c + x, c + y, 8, '190, 255, 110', 0.85);
      break;
    case 3: // polyp
      circle(ctx, c, c, 22);
      fillOutlined(ctx, 0x6a3a5a, 5);
      circle(ctx, c, c, 9);
      ctx.fillStyle = css(0x2a1424);
      ctx.fill();
      break;
    case 4: // rib bone
      ctx.beginPath();
      ctx.arc(c, c + 26, 32, Math.PI * 1.15, Math.PI * 1.85);
      ctx.strokeStyle = css(0x1d1430);
      ctx.lineWidth = 12;
      ctx.lineCap = 'round';
      ctx.stroke();
      ctx.strokeStyle = css(0xe8dcc0);
      ctx.lineWidth = 7;
      ctx.stroke();
      break;
    default: // fungus cap
      circle(ctx, c, c, 24);
      fillOutlined(ctx, 0x7a5a8a, 5);
      for (const [x, y, r] of [[-9, -8, 5], [8, -4, 4], [-2, 9, 4]] as const) {
        circle(ctx, c + x, c + y, r);
        ctx.fillStyle = css(0xd8c8e8);
        ctx.fill();
      }
  }
});

export const envPropRim = makeSheet('env/prop-rim', SIZE, FRAMES, (ctx, c, f) => {
  switch (f) {
    case 0: // floating cube
      ctx.save();
      ctx.translate(c, c);
      ctx.rotate(Math.PI / 4);
      box(ctx, -18, -18, 36, 36, 4);
      fillOutlined(ctx, 0x3c3266, 5);
      ctx.restore();
      glow(ctx, c, c, 10, '200, 170, 255', 0.8);
      break;
    case 1: // shard crystal
      ctx.beginPath();
      ctx.moveTo(c, c - 32); ctx.lineTo(c + 14, c + 4); ctx.lineTo(c, c + 26); ctx.lineTo(c - 14, c + 4);
      ctx.closePath();
      fillOutlined(ctx, 0x8b7ad0, 5);
      ctx.beginPath();
      ctx.moveTo(c, c - 32); ctx.lineTo(c - 14, c + 4); ctx.lineTo(c, c + 2);
      ctx.closePath();
      ctx.fillStyle = css(0xc4b6f0);
      ctx.fill();
      break;
    case 2: // broken pillar
      circle(ctx, c, c, 24);
      fillOutlined(ctx, 0x4a3f78, 5);
      ctx.strokeStyle = css(0x2a2250);
      ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(c - 14, c - 10); ctx.lineTo(c + 2, c + 2); ctx.lineTo(c + 12, c - 12); ctx.stroke();
      break;
    case 3: // orb lamp
      circle(ctx, c, c, 14);
      fillOutlined(ctx, 0xe6d6ff, 4);
      glow(ctx, c, c, 28, '190, 150, 255', 0.6);
      break;
    case 4: // antenna
      ctx.strokeStyle = css(0x1d1430);
      ctx.lineWidth = 8;
      ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(c, c + 26); ctx.lineTo(c, c - 26); ctx.stroke();
      ctx.strokeStyle = css(0x8b7ad0);
      ctx.lineWidth = 4;
      ctx.stroke();
      glow(ctx, c, c - 26, 10, '255, 200, 255', 0.9);
      break;
    default: // glyph stone
      lumpPath(ctx, c, c, 24, 7, 0.12, 0.5);
      fillOutlined(ctx, 0x2e2552, 5);
      ctx.strokeStyle = 'rgba(200, 160, 255, 0.85)';
      ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(c - 8, c - 8); ctx.lineTo(c + 8, c - 8); ctx.lineTo(c, c + 9); ctx.closePath();
      ctx.stroke();
  }
});
